// PRICING — the only file you edit to change prices.
//
// Everything else reads from here. Edit, commit, push. Vercel redeploys in
// about a minute and the site is live with the new numbers.
//
// Two GST conventions are in play and both are deliberate:
//   EQUIPMENT prices below are GST INCLUSIVE, as you quote them.
//   INSTALLATION and MATERIAL rates are EX GST, as your rate card reads and
//   as customers expect to see them. GST on that portion is added as its own
//   visible line on the quote, so the final number is never a surprise.
//
// Razorpay's fee is NOT in these numbers. It is charged to the customer as
// its own line at checkout.

export const PRICES_UPDATED = '2026-09-24';

export const GST_RATE = 0.18;

// Equipment, GST inclusive. Keyed by model id from catalogue.js.
//
// HOW EACH NUMBER IS BUILT, from the 21-09-2026 stock sheet and the
// SEPT-26 Daikin RA discount calculator:
//
//   held in stock   -> your purchase price + 1500
//   ordered in      -> G.TOTAL SET COST + 1500, rounded up to the next 100
//   with-kit, where you hold the G unit
//                   -> without-kit + 1500, because you fulfil it from your
//                      own stock plus a kit. See the note below.
//
// THE WITH-KIT NOTE. Applying the ordered-in rule to the F variant of a
// model you already stock pushes GTQ50 with kit to 43,100 against 35,100
// without — an 8,000 gap for 3 m of copper. That breaks three things at
// once: KIT_ADDON lets anyone buy the G unit plus a kit for 36,600 and the
// F variant never sells; the "cheaper on a fresh install" logic under
// KIT_COPPER_METRES stops being true; and ProductDetail's savings line
// becomes a claim a customer can disprove. So the seven models you hold G
// stock of are priced at without-kit + 1500 instead.
//
// If you would rather price the F variant off Daikin's current list, swap
// these seven in and nothing else changes:
//   gtq-35 withKit 35300 · gtq-50 43100 · gtkc-35 35500 · gtkc-50 39200
//   gtkl-60 52600 · gtkl-71 61100 · gtkm-35 42000
//
// EITHER WAY: when a G variant's stock reaches zero, move BOTH variants of
// that model to the ordered-in numbers above. Until then you are selling a
// unit you already own at a cost you already paid.
export const PRICES = {
      "gtkl-35": {
      "withoutKit": 31700         // SOLD OUT 28-10. Old-stock price until Saheb gives the next-batch price. MRP 46,700 (14,100 + 32,600)
    },
  "ftku-50": {
    "withKit": 47400            // SOLD OUT 05-11, next batch: 46,400 + 1,000 (Saheb 05-11)
  },
  "gtq-28": {
    "withoutKit": 27700,        // ordered in
    "withKit": 29200            // ordered in
  },
  "gtq-35": {
    "withoutKit": 30900,        // stock 5
    "withKit": 31400
  },
  "gtq-50": {
    "withoutKit": 36100,        // stock 21
    "withKit": 36600
  },
  "gtkc-35": {
    "withoutKit": 33900,        // SOLD OUT 03-11, ordered in. With-kit set cost less Daikin's F-G dealer price gap (41,600 - 40,000 = 1,600), + 1500, rounded up. Was 31,600. Confirm on the next Daikin calculator
    "withKit": 35500            // SOLD OUT 03-11, ordered in: SEPT-26 Daikin calculator (G.TOTAL SET COST + 1500, rounded up). Was 32,100
  },
  "gtkc-50": {
    "withoutKit": 37800,        // SOLD OUT 13-10, ordered in (moved 26-10). With-kit set cost less Daikin's F-G dealer price gap (47,400 - 46,000 = 1,400), + 1500, rounded up. Confirm on the next Daikin calculator
    "withKit": 36900            // stock 2 (FTKC50XV16VLA, 10 added 02-11-2026), Saheb's price. Next unit 39,200 (SEPT-26 Daikin calculator): see NEXT_PRICE
  },
  "gtkl-50": {
    "withoutKit": 38000,        // SOLD OUT 29-10, ordered in: SEPT-26 Daikin calculator (G.TOTAL SET COST + 1500, rounded up). Was 36,000 in stock
    "withKit": 40200            // SOLD OUT 29-10, ordered in: SEPT-26 Daikin calculator. Was 36,500
  },
  "gtkl-60": {
    "withoutKit": 46600,        // stock 6
    "withKit": 47100
  },
  "gtkl-71": {
    "withoutKit": 59900,        // SOLD OUT 26-10, ordered in. Set by Saheb 26-10
    "withKit": 59900            // stock 2 (FTKL71XV16MAA, 02-11-2026), Saheb's price. Next unit 61,000 (his ordered-in price): see NEXT_PRICE
  },
  "gtkl-90": {
    "withKit": 84500            // stock 1, old-stock price. Next unit 86,500: see NEXT_PRICE
  },
  "gtkm-35": {
    "withoutKit": 38200,        // stock 4
    "withKit": 38700
  },
  "gtkm-50": {
    "withoutKit": 50700,        // ordered in — stock ran out
    "withKit": 52200            // ordered in
  },
  "gtkm-60": {
    "withoutKit": 66000,        // ordered in. 23-10-2026, Saheb's price (was 68,400)
    "withKit": 66800            // stock 1 (FTKM60XV16MAB), Saheb's price 02-11-2026 (was 66,600). Next unit 67,600: see NEXT_PRICE
  },
  "ftht-35": {
    "withKit": 35600            // stock 2
  },
  "ftht-50": {
    "withKit": 41900            // 05-11-2026, Saheb's new price (was 41,200). Stock held at 1 until he confirms the count
  },
  "ftht-60": {
    "withKit": 51700            // stock 1. Next unit 51,700: see NEXT_PRICE
  },
  "jtkj-35": {
    "withKit": 46800            // ordered in
  },
  "jtkj-53": {
    "withKit": 58200            // ordered in
  },
  "jtkj-60": {
    "withKit": 73800            // ordered in
  },

  // CLEARANCE (old stock, src/data/clearance.js). Purchase price + 600,
  // rounded up to the next 500. GST inclusive.
  "cl-asgg18cpaa-b": { "withoutKit": 43000 },
  "cl-asgg18ckaa-b": { "withoutKit": 38000 },
  "cl-asgg12jlcab": { "withoutKit": 33500 },
  "cl-asgg22ckaa-b": { "withoutKit": 48000 },
  "cl-ras-e518pccib1": { "withKit": 37000 },
  "cl-ras-b222pcaiba1": { "withKit": 40000 },
  "cl-gtky35uv16w3": { "withoutKit": 28000 }
};

// Units in hand, PER VARIANT. Drives the 24-hour delivery badge.
// With-kit and without-kit are separate SKUs, so stock them separately — a
// model can be promised for tomorrow in one variant and not the other.
//
// THREE STATES, and the number alone decides which:
//   above 0      in stock: 24-hour delivery, Add to cart / Buy now.
//   exactly 0    SOLD OUT: the card and the product page say "Sold out".
//                Customers can still request one; it is ordered from Daikin
//                for them.
//   key deleted  a normal on-order unit, nothing said about selling out.
//                Delete the key once "Sold out" is no longer worth showing.
//
// THE COUNT MOVES BY ITSELF (Oct 2026). These numbers are your sheet, counted
// at STOCK_AS_OF. Every paid online order after that time is taken off
// automatically: the payment server checks it before taking money
// (api/_stock.js), and every build subtracts it before the site is made
// (scripts/apply-online-sales.js). So when a new sheet comes in, set the
// numbers AND the time you counted. Online sales before that time are already
// in your count and are not taken off twice.
export const STOCK_AS_OF = '2026-11-05T12:32:02.423Z';  // moved by the build: 2 online orders and stock page changes taken in

// From the stock sheet dated 28-10-2026 (no time on it, so counted from the
// start of that day: an online sale on the 28th can come off twice, never
// be missed). Two lines keep the lower number because the sheet does not yet
// show a sale Saheb reported on 19-10: GTKC35 (sheet 2, 1 sold offline) and
// the 2023 ASGG18CKAA-B (sheet 3, 1 sold online).
export const STOCK = {
  "gtkl-35": {
    "withoutKit": 0             // SOLD OUT 28-10-2026 (was 2)
  },
  "ftku-50": {
    "withKit": 0                // SOLD OUT 05-11-2026: the 2 for sale sold (also recorded on the stock page); the third is held for a customer
  },
  "gtq-35": {
    "withoutKit": 5
  },
  "gtq-50": {
    "withoutKit": 21            // was 26
  },
  "gtkc-35": {
    "withoutKit": 0             // SOLD OUT 03-11-2026
  },
  "gtkc-50": {
    "withoutKit": 0,          // SOLD OUT
    "withKit": 2              // FTKC50XV16VLA: 10 added 02-11-2026, 8 sold by 05-11 (Saheb)
  },
  "gtkl-50": {
    "withoutKit": 0             // SOLD OUT 29-10-2026: 2 sold (sheet 28-10 had 2)
  },
  "gtkl-60": {
    "withoutKit": 6             // was 7
  },
  "gtkl-71": {
    "withoutKit": 0,            // SOLD OUT 26-10-2026
    "withKit": 2                // FTKL71XV16MAA, added 02-11-2026
  },
  "gtkm-35": {
    "withoutKit": 4             // was 3
  },
  "gtkm-60": {
    "withKit": 1                // FTKM60XV16MAB, on the 28-10 sheet; confirmed 02-11
  },
  "ftht-35": {
    "withKit": 2
  },
  "ftht-50": {
    "withKit": 0                // 03-11-2026: last confirmed 3 (29-10); Saheb unsure after today's sales, so held at 1 until he confirms
  },
  "ftht-60": {
    "withKit": 0                // 03-11-2026, Saheb: only 1 left (was 7)
  },
  "gtkl-90": {
    "withKit": 1              // added 15-10-2026, priced 84,500 (last unit)
  },

  // CLEARANCE (old stock, src/data/clearance.js). Same rules as above: online
  // sales come off by themselves, 0 shows "Sold out".
  "cl-asgg18cpaa-b": {
    "withoutKit": 3             // was 4
  },
  "cl-asgg18ckaa-b": {
    "withoutKit": 2             // 2023 batch. Sheet says 3; 1 was sold online 19-10 (Razorpay)
  },
  "cl-asgg12jlcab": {
    "withoutKit": 1
  },
  "cl-asgg22ckaa-b": {
    "withoutKit": 0           // SOLD OUT 14-10-2026
  },
  "cl-ras-e518pccib1": {
    "withKit": 1
  },
  "cl-ras-b222pcaiba1": {
    "withKit": 2
  },
  "cl-gtky35uv16w3": {
    "withoutKit": 0             // SOLD OUT 19-10 (offline)
  }
};

// NEXT PRICE: what the next unit costs once the stock you hold is gone.
//
// Set it on a unit you are selling below its replacement price. While that
// unit is in stock the site shows the approved call-out: "Last unit at this
// price" with this next price on the product page (price ladder), the shop
// card (amber tag) and the cart. The moment its stock reaches 0, sold online
// or set by hand, the build switches its price in PRICES to this one
// (scripts/apply-online-sales.js), so it never shows as sold out at the old
// price. Delete the line once you have restocked at the new price.
//
// NEXT BATCH SOONER THAN USUAL. A unit not in stock normally says "delivered
// in 5 to 7 days". Put it in NEXT_BATCH (below NEXT_PRICE) when its next
// batch is due sooner: the shorter time shows on the shop card, the product
// page, the collection pages, the AC calculator and the Google feed until the
// `until` date (that whole day, India time), then it goes back to 5 to 7 days
// by itself. leadTimeFor() in lib/listing.js and scripts/product-offer.js
// read it.
export const NEXT_PRICE = {
  "gtkl-90": {
    "withKit": 86500          // 15-10-2026: last unit at 84,500
  },
  "gtkm-60": {
    "withKit": 67600          // 28-10-2026: 1 FTKM60 in stock, 66,800 from 02-11
  },
  "gtkc-50": {
    "withKit": 39200          // 02-11-2026: 10 FTKC50 in stock at 36,900
  },
  "gtkl-71": {
    "withKit": 61000          // 02-11-2026: 2 FTKL71 in stock at 59,900
  },
  "ftht-60": {
    "withKit": 51700          // 03-11-2026, Saheb: next batch 1,000 more than the last unit at 50,700
  }
};

// See "NEXT BATCH SOONER THAN USUAL" above NEXT_PRICE.
// 03-11-2026, Saheb: "Next batch in 3-4 days for gtkl" (the without-kit GTKL
// units). Shown until Sat 7 Nov, then back to 5 to 7 days.
export const NEXT_BATCH = {
  "gtkl-35": { "withoutKit": { "days": "3 to 4", "until": "2026-11-07" } },
  "gtkl-50": { "withoutKit": { "days": "3 to 4", "until": "2026-11-07" } },
  "gtkl-60": { "withoutKit": { "days": "3 to 4", "until": "2026-11-07" } },
  "gtkl-71": { "withoutKit": { "days": "3 to 4", "until": "2026-11-07" } }
};

// Installation and materials. EX GST. From your rate card.
export const RATES = {
  installSplit:    { label: 'Installation, split AC', price: 1500, per: 'unit' },
  installCassette: { label: 'Installation, cassette', price: 3500, per: 'unit' },
  nitrogenTest:    { label: 'Nitrogen pressure test and flushing', price: 800, per: 'unit' },
  copperSplit:     { label: 'Copper piping with insulation', price: 1250, per: 'metre' },
  copperCassette:  { label: 'Copper piping with insulation, cassette', price: 1400, per: 'metre' },
  standSplit1:     { label: 'MS angle iron stand, 1 TR', price: 750, per: 'unit' },
  standSplit15:    { label: 'MS angle iron stand, 1.5 to 2 TR', price: 900, per: 'unit' },
  standCassette:   { label: 'MS angle iron stand, cassette', price: 1000, per: 'unit' },
  dismantleOld:    { label: 'Dismantling of existing unit', price: 650, per: 'unit' },
  antiCorrosion:   { label: 'Anti-corrosion treatment', price: 2500, per: 'unit' },
  drainPipe:       { label: 'PVC drain piping', price: 170, per: 'metre' },
  controlWiring:   { label: 'Control wiring', price: 170, per: 'metre' },
  chiseling:       { label: 'Chiseling work', price: 155, per: 'metre' },
};

// The original Daikin piping kit, sold as a separate item to someone buying a
// without-kit unit. Set `available: false` when you have no kits in hand and
// the option disappears from the product page and the cart.
export const KIT_ADDON = {
  label: 'Original Daikin piping kit',
  price: 1500,
  gstInclusive: true,
  available: false,
  note: 'Genuine Daikin kit with 3 m insulated copper. Only worth adding if the existing piping cannot be reused.',
};

// Delivery is not a fixed number — it depends on distance, floor and access.
// It is quoted at actuals with the AC rather than guessed here, and every
// total says so rather than quietly leaving it out.
export const DELIVERY_CHARGE = {
  label: 'Delivery',
  onActuals: true,
  note: 'Charged at actuals, confirmed with the AC before dispatch.',
};

// The Daikin piping kit covers this many metres. Anything beyond is charged
// at the copperSplit rate. This is why a with-kit unit works out cheaper on
// a fresh install: the kit costs 1500 and 3 m of your copper costs 3750.
export const KIT_COPPER_METRES = 3;

// Mandatory on every installation. Shown on the quote, not removable.
export const MANDATORY_ADDONS = ['nitrogenTest'];

// Customer-selectable. Order here is the order they appear on the page.
export const OPTIONAL_ADDONS = ['standSplit15', 'dismantleOld', 'antiCorrosion'];

// Charged by the metre and measured on site. These are NOT added to the
// headline price by default — the customer almost never knows their pipe run,
// and guessing a length inflates the quote with a number they did not choose.
// The page shows the rate; the quantity is confirmed at survey. A customer who
// does know their measurements can enter them and see the total update.
// Three separate runs, three separate lengths. Copper goes indoor to outdoor,
// drain takes its own route to a drain point, control wiring follows the copper
// but rarely matches it. Charging one figure across all three is wrong and
// produces a total nobody can check.
export const MEASURED_ON_SITE = [
  { key: 'copperSplit', field: 'copperM', label: 'Copper piping' },
  { key: 'drainPipe', field: 'drainM', label: 'Drain piping' },
  { key: 'controlWiring', field: 'wireM', label: 'Control wiring' },
];

// Your picks for the "Top sellers" strip, in the order you want them shown.
// Each entry is "<model id>:<kit>" — the same key the grid uses.
// Empty array and the strip disappears.
export const TOP_SELLERS = [
  'gtkl-50:withoutKit',         // pinned first 21-10-2026
  'gtq-50:withoutKit',
  'ftht-60:withKit',
  'gtkc-50:withoutKit',
  'ftht-50:withKit',
];

// "Only N left at this price" shows at or below this quantity. Above it the
// card reads "Limited stock at this price". Both are true of every stocked
// unit right now: each is 2,400 to 5,800 under what the next one costs to
// buy in, so the price genuinely moves when the stock runs out.
export const LOW_STOCK_AT = 5;

// How many cards the "Great discounts" strip carries.
export const DISCOUNT_COUNT = 4;

// Delivery promise. The badge only shows when STOCK for that model is above
// zero — set stock to 0 or delete the key and the badge disappears by itself.
export const DELIVERY = {
  badge: '24-hour delivery',
  badgeShort: '24 hr',
  footnote:
    '24-hour delivery applies to orders placed Monday to Friday, excluding public holidays. Saturday orders dispatch the next working day.',
};


// EXCHANGE — an old AC taken in part-payment against a new one.
//
// Value comes from the OLD unit's tonnage and coil material, not the new one.
// A copper-coil machine has real scrap value; aluminium does not, which is why
// aluminium is flat rather than graded by size.
//
// Uninstallation is free when the customer is exchanging: the engineer is
// taking the old unit away regardless, so charging dismantleOld on top would
// be billing twice for one visit. quote.js waives it automatically.
export const EXCHANGE = {
  enabled: true,
  label: 'Exchange your old AC',
  copper: [
    { maxTon: 1.0, value: 6000, label: '1 ton and below' },
    { maxTon: 1.5, value: 6500, label: '1.5 ton' },
    { maxTon: 99, value: 7000, label: '2 ton and above' },
  ],
  aluminium: 3500,
  note: 'Value confirmed on inspection at pickup. The unit must be complete, with both indoor and outdoor units and the compressor intact.',
  conditions: [
    'Any brand accepted',
    'Both indoor and outdoor units required',
    'Free uninstallation of the old unit',
  ],
};

// tokenAmount is the old 2000 advance idea and nothing reads it any more.
// The 499 price lock above replaces it. Delete tokenAmount and
// tokenPaymentUrl once you have confirmed nothing else references them.
export const PAYMENT = {
  equipment: 'Full payment before dispatch',
  installation: 'Surveyed on site and quoted separately, after your order',
  tokenAmount: 2000,
  tokenPaymentUrl: '',
  quoteValidDays: 7,
};

export const OFFER = {
  active: false,
  headline: 'Off-season pricing',
  endsOn: '',
  note: '',
};

export const BUSINESS = {
  name: 'Citi Cool Corporation',
  phone: '+917703900307',
  phoneDisplay: '+91 77039 00307',
  whatsapp: '917703900307',
  email: 'admin@citicool.in',
  gstin: '07AAJPS6580J1Z2',
  address: { street: '13 Jain Mandir, South Extension Part 2', city: 'New Delhi', postalCode: '110049', country: 'IN' },
  since: 1990,
  daikinSince: 2003,
  projects: 1500,
  credentials: ['Authorised Daikin dealer', 'Daikin No. 1 dealer, North region', 'MD Club Gold Member'],
  serviceAreas: ['Delhi', 'Noida', 'Gurgaon', 'Faridabad'],
};